import React, { useEffect, useState, useRef } from 'react';

import Highcharts from 'highcharts/highstock';
import { useIntl, defineMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { constants } from '@nhl/formatter-utilities';

import { gameShiftSelectors } from '../../../../../redux/ducks/gameShifts';
import { commonMessages } from '../../../../../utility/commonMessages';

import ShiftChartButtonsContainer from './components/shiftChartButtonsContainer';

const ShiftChartContainer = () => {
    const intl = useIntl();
    const chartNode = useRef(null);
    const [chart, setChart] = useState(null);
    const { game, goals, shifts, teamData } = useSelector(({ gameShifts }) => gameShifts);
    const { gameFormat } = game;

    useEffect(() => {
        createChart();
    }, []);

    const initializeChartSeries = (processedShifts) => {
        // add dummy teamId series for the legend
        const teamIds = processedShifts.map((row) => row.teamId);
        const teamIdList = [...new Set(teamIds)];

        const series = teamIdList.map((teamId) => {
            const { color } = processedShifts.find((x) => x.teamId === teamId);
            return {
                id: `team_${teamId}`,
                data: [],
                color,
                name: `${teamId}`,
                showInLegend: true,
                selected: true,
            };
        });

        const processedShiftData = processedShifts.reverse().map((shiftObj, i) => {
            const item = {
                name: shiftObj.name,
                color: shiftObj.color,
                linkedTo: `team_${shiftObj.teamId}`,
                data: [],
            };

            shiftObj.intervals.forEach((interval, j) => {
                item.data.push(
                    {
                        x: interval.from,
                        y: i,
                        from: interval.from,
                        to: interval.to,
                        eventType: 'shift',
                    },
                    {
                        x: interval.to,
                        y: i,
                        from: interval.from,
                        to: interval.to,
                        eventType: 'shift',
                    },
                );

                // add a null value between intervals
                if (shiftObj.intervals[j + 1]) {
                    item.data.push([(interval.to + shiftObj.intervals[j + 1].from) / 2, null]);
                }
            });

            return item;
        });

        return series.concat(processedShiftData);
    };

    const initializeGoalSeries = () => {
        const maxPlayerCount = new Set(shifts.map((row) => row.playerId)).size;

        const goalSeriesData = goals
            .filter((row) => row.eventDescription !== 'Shootout')
            .map((goalObject, idx) => {
                return {
                    x: gameShiftSelectors.utcDateForTimeStringAndPeriod(goalObject.startTime, goalObject.period),
                    y: maxPlayerCount + 1,
                    color: goalObject.hexValue,
                    goalDataIndex: idx,
                    eventType: 'goal',
                    teamId: goalObject.teamId,
                    period: goalObject.period,
                    endTime: goalObject.endTime,
                };
            });

        return {
            showInLegend: true,
            name: intl.formatMessage(commonMessages.labels.goals),
            type: 'column',
            color: '#000000',
            // pointRange: 3600 * 1000 * 60, // XXX Idk why this won't work, it works in prod, not locally
            pointWidth: 2,
            data: goalSeriesData,
            selected: true,
        };
    };

    const setPeriodTimeRange = (periodIndex, gameType, periods) => {
        // Used to decipher final period and final period length
        const orderedGoalData = goals.sort((a, b) => a.eventNumber - b.eventNumber);
        const finalGoal = orderedGoalData[orderedGoalData.length - 1];
        const finalPeriod = finalGoal.period;

        const regularPeriodLength = 20;
        const regularOTLength = 5;
        const currentPeriodLength = periodIndex * regularPeriodLength;

        let periodMinutesLength;
        let periodSecondsLength = 0;
        if (gameType !== constants.PLAYOFFS && periods.length > 4 && periodIndex >= finalPeriod - 1) {
            // include "Full Game"
            // Pre or Regular Season, No OT or SO
            periodMinutesLength = currentPeriodLength + parseInt(finalGoal.endTime.split(':')[0]);
            periodSecondsLength = parseInt(finalGoal.endTime.split(':')[1]);
        } else if (gameType === constants.PLAYOFFS && periodIndex === finalPeriod - 1) {
            // Post Season, Any OT as final period.
            periodMinutesLength = currentPeriodLength + parseInt(finalGoal.endTime.split(':')[0]);
            periodSecondsLength = parseInt(finalGoal.endTime.split(':')[1]);
        } else if (gameType !== constants.PLAYOFFS && periodIndex === 3) {
            // Pre or Regular Season OT
            periodMinutesLength = currentPeriodLength + regularOTLength;
        } else {
            // Pre or Regular Season, No OT or SO.
            periodMinutesLength = currentPeriodLength + regularPeriodLength;
        }

        const periodStartTime = Date.UTC(0, 0, 0, 0, currentPeriodLength, periodSecondsLength);
        const periodEndTime = Date.UTC(0, 0, 0, 0, periodMinutesLength, periodSecondsLength);

        // Update chart range
        chart.xAxis[0].setExtremes(periodStartTime, periodEndTime);

        //redraws the chart
        chart.redraw();
    };

    const resetChartZoom = () => {
        chart.xAxis[0].setExtremes(null, null);
    };

    const createChart = () => {
        const shift = shifts[0];

        const processedShifts = gameShiftSelectors.getProcessedShifts(shifts);
        const chartSeries = initializeChartSeries(processedShifts);
        const goalSeries = initializeGoalSeries();
        const series = chartSeries.concat(goalSeries);
        const playerCount = chartSeries.length - 2;

        // create the chart
        const shiftChart = new Highcharts.Chart({
            chart: {
                renderTo: chartNode.current,
                height: 900,
                zoomType: 'x',
            },

            credits: {
                enabled: false,
            },

            title: {
                text: intl.formatMessage(
                    defineMessage({
                        defaultMessage: 'Shifts',
                        description: 'gameShifts.shifts',
                    }),
                ),
            },

            xAxis: {
                type: 'datetime',
                labels: {
                    formatter: ({ value }) => {
                        if (Highcharts.dateFormat('%H:%M:%S', value).split(':')[0] > '00') {
                            return Highcharts.dateFormat('%l:%M:%S', value);
                        }
                        return Highcharts.dateFormat('%M:%S', value);
                    },
                },
            },

            yAxis: [
                {
                    tickInterval: 1,
                    startOnTick: false,
                    endOnTick: false,
                    title: {
                        text: '',
                    },
                    min: -1,
                    max: playerCount + 1,
                    labels: {
                        formatter: ({ value }) => {
                            return processedShifts[value] ? processedShifts[value].name : '';
                        },
                    },
                },
            ],

            legend: {
                enabled: true,
                rtl: true,
                useHTML: true,
                itemMarginTop: 20,
                borderWidth: 1,
                borderColor: '#000000',
                title: {
                    text: 'Legend:',
                },
                verticalAlign: 'middle',
                layout: 'vertical',
                align: 'right',
                // Using a function for correct this access
                labelFormatter: function () {
                    return this.options.type === 'column'
                        ? this.name
                        : gameShiftSelectors.createHtmlTeamDisplay(shift.gameId, this.name, teamData, 'legend-logo');
                },
            },

            responsive: {
                rules: [
                    {
                        condition: {
                            maxWidth: 1024,
                        },
                        chartOptions: {
                            legend: {
                                rtl: false,
                                margin: 35,
                                itemMarginBottom: 0,
                                itemDistance: 0,
                                align: 'center',
                                verticalAlign: 'top',
                                layout: 'horizontal',
                            },
                        },
                    },
                ],
            },

            tooltip: {
                enabled: true,

                // Using a function for correct this access
                formatter: function () {
                    if (!this.y) {
                        return false;
                    }

                    if (this.point.options.eventType && this.point.options.eventType === 'goal') {
                        const goalData = goals[this.point.options.goalDataIndex];
                        const goalString = `<b>GOAL: </b>${goalData.firstName} ${goalData.lastName}`;
                        const timeString = `Period ${goalData.period}, ${goalData.startTime}`;

                        return `${goalString}<br/>${timeString}`;
                    }

                    if (processedShifts[this.y] && processedShifts[this.y].name) {
                        const timeFrom =
                            Highcharts.dateFormat('%H:%M:%S', this.point.options.from).split(':')[0] > '00'
                                ? Highcharts.dateFormat('%l:%M:%S', this.point.options.from)
                                : Highcharts.dateFormat('%M:%S', this.point.options.from);

                        const timeTo =
                            Highcharts.dateFormat('%H:%M:%S', this.point.options.to).split(':')[0] > '00'
                                ? Highcharts.dateFormat('%l:%M:%S', this.point.options.to)
                                : Highcharts.dateFormat('%M:%S', this.point.options.to);

                        return `<b>${processedShifts[this.y].name}</b><br/>${timeFrom}  - ${timeTo}`;
                    }
                },
                positioner: (labelWidth, labelHeight, point) => {
                    const halfHeight = shiftChart.plotHeight / 2;
                    const halfWidth = shiftChart.plotWidth / 2;

                    const tooltipX = point.plotX > halfWidth ? point.plotX - labelWidth : point.plotX;
                    const tooltipY = point.plotY > halfHeight ? point.plotY - labelHeight : point.plotY;

                    return {
                        x: tooltipX + shiftChart.plotLeft,
                        y: tooltipY + shiftChart.plotTop,
                    };
                },
            },

            plotOptions: {
                line: {
                    lineWidth: 10,
                    marker: {
                        enabled: false,
                    },
                    dashStyle: 'Solid',
                    grouping: false,
                },
                column: {
                    dataLabels: {
                        enabled: true,
                        allowOverlap: true,
                        crop: false,
                        overflow: 'none',
                        // Using a function for correct this access
                        formatter: function () {
                            const { teamId } = this.point.options;
                            return gameShiftSelectors.createHtmlTeamDisplay(
                                shift.gameId,
                                teamId,
                                teamData,
                                'plot-line-logo',
                            );
                        },
                        y: 3,
                        zIndex: 2,
                        useHTML: true,
                    },
                },
                series: {
                    borderRadius: 0,
                    events: {
                        checkboxClick: (event) => {
                            if (event.checked) {
                                event.item.show();
                            } else {
                                event.item.hide();
                            }

                            const goalsSeries = shiftChart.series.filter((x) => x.name === 'Goals')[0];
                            const [team1, team2] = shiftChart.series.filter(
                                (x) => x.userOptions.id && x.userOptions.id.indexOf('team_') === 0,
                            );
                            const team1StartIndex = shiftChart.series.findIndex(
                                (x) => x.userOptions.linkedTo === team1.userOptions.id,
                            );
                            const team1Size = shiftChart.series.filter(
                                (x) => x.userOptions.linkedTo === team1.userOptions.id,
                            ).length;

                            const updateSeriesPoints = (seriesElement, value) => {
                                for (let i = 0; i < seriesElement.points.length; i++) {
                                    seriesElement.points[i].y = value;
                                }
                            };
                            // Reset Goals Series Y Position
                            updateSeriesPoints(goalsSeries, goalsSeries.index - 1);

                            if (team1.visible && !team2.visible) {
                                shiftChart.yAxis[0].setExtremes(team1Size, playerCount + 1);
                            } else if (!team1.visible && team2.visible) {
                                updateSeriesPoints(goalsSeries, team1StartIndex - 3);
                                shiftChart.yAxis[0].setExtremes(-1, team1Size - 1);
                            } else {
                                shiftChart.yAxis[0].setExtremes(-1, playerCount + 1);
                            }

                            // redraws the chart
                            shiftChart.redraw();
                        },
                        legendItemClick: () => false,
                    },
                    showCheckbox: true,
                },
            },

            navigator: {
                enabled: true,
                xAxis: {
                    labels: {
                        formatter: ({ value }) => {
                            return Highcharts.dateFormat('%H:%M:%S', value).split(':')[0] > '00'
                                ? Highcharts.dateFormat('%l:%M:%S', value)
                                : Highcharts.dateFormat('%M:%S', value);
                        },
                    },
                },
            },

            series,
        });
        setChart(shiftChart);
    };

    return (
        <div className="shift-chart-container">
            <div className="shift-chart-outer-wrapper">
                <div id="PlayerComparisonChart" ref={chartNode} />
                <ShiftChartButtonsContainer
                    chartData={shifts}
                    gameFormat={gameFormat}
                    onPeriodButtonClick={setPeriodTimeRange}
                    onFullGameButtonClick={resetChartZoom}
                />
            </div>
        </div>
    );
};

export default ShiftChartContainer;
